import type React from "react"

import { Popover, PopoverContent, PopoverTrigger } from "@coval/ui"

import type { JsonFieldValue } from "@/types/json"

import { TruncatedCell } from "./TruncatedCell"

const TRIGGER_CLASS_NAME =
  "block w-full max-w-[200px] cursor-pointer rounded p-1 text-left hover:bg-surface-1"

const CONTENT_CLASS_NAME = "max-w-[400px] max-h-[300px] overflow-auto"

const ExpectedOutputPopover = ({
  trigger,
  children,
}: {
  trigger: React.ReactNode
  children: React.ReactNode
}) => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" className={TRIGGER_CLASS_NAME}>
        {trigger}
      </button>
    </PopoverTrigger>
    <PopoverContent
      side="bottom"
      align="start"
      aria-label="Expected output"
      className={CONTENT_CLASS_NAME}
    >
      {children}
    </PopoverContent>
  </Popover>
)

export const ExpectedOutputJsonCell = ({
  value,
}: {
  value: JsonFieldValue | undefined
}) => {
  if (
    value == null ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === "object" &&
      !Array.isArray(value) &&
      Object.keys(value).length === 0) ||
    (typeof value === "string" && value.trim().length === 0)
  ) {
    return <span className="text-coval-text-muted">-</span>
  }

  if (typeof value !== "object" || Array.isArray(value)) {
    const displayValue =
      typeof value === "string" ? value : JSON.stringify(value, null, 2)

    return (
      <ExpectedOutputPopover
        trigger={<TruncatedCell value={displayValue} maxLines={2} />}
      >
        <pre className="text-sm text-coval-text whitespace-pre-wrap break-all">
          {displayValue}
        </pre>
      </ExpectedOutputPopover>
    )
  }

  const entries = Object.entries(value)
  const displayLimit = 3
  const hasMore = entries.length > displayLimit

  return (
    <ExpectedOutputPopover
      trigger={
        <span className="block space-y-0.5">
          {entries.slice(0, displayLimit).map(([key, val]) => (
            <span key={key} className="flex items-center gap-1 text-sm">
              <span className="text-coval-text-muted font-medium truncate max-w-[60px]">
                {key}:
              </span>
              <span className="truncate text-coval-text">
                {typeof val === "object" ? JSON.stringify(val) : String(val)}
              </span>
            </span>
          ))}
          {hasMore && (
            <span className="block text-xs text-coval-text-muted">
              +{entries.length - displayLimit} more
            </span>
          )}
        </span>
      }
    >
      <div className="space-y-1">
        {entries.map(([key, val]) => (
          <div key={key} className="flex gap-2 text-sm">
            <span className="text-coval-text-muted font-medium shrink-0">
              {key}:
            </span>
            <span className="text-coval-text break-all">
              {typeof val === "object"
                ? JSON.stringify(val, null, 2)
                : String(val)}
            </span>
          </div>
        ))}
      </div>
    </ExpectedOutputPopover>
  )
}
